import { memo, useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  loadProfile,
  loadWishlistIds,
  useMembership,
} from '@techstyle/react-accounts';

import { loadProducts } from '../../actions/products';
import { getProductFilterState } from '../../utils/selectors';

function Products({
  applyUserSizes,
  filters,
  initialLoad,
  initialStoreKey,
  isFavoritesContext,
  isFromFavorites: isFromFavoritesFromProps = false,
  onLoadedProducts,
  onLoadProfile = () => {},
  onTransformFilters = () => {},
  pageLimit,
  render,
  shouldCategorize: shouldCategorizeFromProps = false,
}) {
  const dispatch = useDispatch();
  const { isVisitor, networkStatus } = useMembership();
  const isMembershipInitialized = !!networkStatus.isUpToDate;
  const {
    canPaginate,
    didTransformFilters,
    filters: filterState,
    inputFilters,
    products,
    refinements,
    storeKey,
  } = useSelector(state =>
    getProductFilterState(state, {
      filters,
      applyUserSizes,
      isFavoritesContext,
    })
  );
  const [error, setError] = useState(null);
  const [fetchedKey, setFetchedKey] = useState(initialStoreKey);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);

  const loadPage = useCallback(
    async (
      pageNumber = 1,
      params = filterState,
      aggs = true,
      isFromFavorites = isFromFavoritesFromProps,
      shouldCategorize = shouldCategorizeFromProps
    ) => {
      setPageNumber(pageNumber);
      if (pageNumber > 1) {
        aggs = false;
      }
      setLoading(true);
      let result;
      let error;

      try {
        result = await dispatch(
          loadProducts({ page: pageNumber, ...params }, aggs, {
            shouldCategorize,
            isFromFavorites,
          })
        );
      } catch (err) {
        error = err;
      }

      // FIXME: Remain loading if there was an error, so we don't keep trying to
      // fetch additional pages.
      setError(error ? result.payload : null);
      setFetchedKey(storeKey);
      setLoading(false);
    },
    [
      dispatch,
      filterState,
      isFromFavoritesFromProps,
      shouldCategorizeFromProps,
      storeKey,
    ]
  );

  useEffect(() => {
    setFetchedKey(initialStoreKey);
  }, [initialStoreKey]);

  useEffect(() => {
    onTransformFilters(filterState, inputFilters);
  }, [didTransformFilters, onTransformFilters]);

  useEffect(() => {
    // `XtraShopLayout` determines which asset to show based on `products` inventory
    onLoadedProducts && onLoadedProducts(products);
  }, [products]);

  useEffect(() => {
    if (storeKey !== fetchedKey) {
      loadPage(1);
    }
  }, [fetchedKey, loadPage, storeKey]);

  useEffect(() => {
    if (isMembershipInitialized && !isVisitor) {
      dispatch(loadWishlistIds());
    }
  }, [dispatch, isMembershipInitialized, isVisitor]);

  useEffect(() => {
    const fetchData = async () => {
      didTransformFilters &&
        onTransformFilters &&
        onTransformFilters(filterState);
      const shouldLoadPage = storeKey !== fetchedKey;

      if (!shouldLoadPage) {
        setLoading(false);
      }
      if (applyUserSizes) {
        await dispatch(loadProfile());
      }
      onLoadProfile();

      if (isMembershipInitialized && !isVisitor) {
        dispatch(loadWishlistIds());
      }

      if (shouldLoadPage) {
        loadPage(1);
      }
    };
    fetchData();
  }, [
    applyUserSizes,
    didTransformFilters,
    isMembershipInitialized,
    isVisitor,
    onTransformFilters,
  ]);

  return render({
    hasNextPage:
      !loading &&
      !error &&
      canPaginate &&
      (!pageLimit || pageNumber < pageLimit),
    loadPage: loadPage,
    initialLoad,
    products,
    loading,
    refinements,
    storeKey,
    pageNumber,
  });
}

Products.propTypes = {
  /**
   * Whether to attempt to transform the input filters to populate the user's
   * preferred sizes when possible.
   */
  applyUserSizes: PropTypes.bool,
  filters: PropTypes.object,
  /**
   * The selected filters
   */
  initialLoad: PropTypes.object,
  initialStoreKey: PropTypes.string,
  isFavoritesContext: PropTypes.bool,
  isFromFavorites: PropTypes.bool,
  /**
   * A callback to pass the resulting `products` back to the parent.
   */
  onLoadedProducts: PropTypes.func,
  onLoadProfile: PropTypes.func,
  /**
   * A callback to inform the parent that the input `filters` to the component
   * were changed by this component, so the parent can update any filter state
   * it keeps.
   */
  onTransformFilters: PropTypes.func,
  pageLimit: PropTypes.number,
  /**
   * props that determine what is going to be rendered.
   */
  render: PropTypes.func.isRequired,
  shouldCategorize: PropTypes.bool,
};

export default memo(Products);
